import {
  canApplyReportingUnitOverride,
  CanApplyReportingUnitOverrideResult,
} from "encamp-shared/src/units/canApplyReportingUnitOverride";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { ReportingFacilityChemicalQuery, UnitType } from "generated-graphql/graphql";

import {
  toPrismaDensityUnits,
  toPrismaStateOfMatter,
  toPrismaUnitType,
} from "./reportingUnitEnums";

type Rfc = ReportingFacilityChemicalQuery["reportingFacilityChemical"];

type OverrideRefusal = Extract<CanApplyReportingUnitOverrideResult, { ok: false }>;

/**
 * Whether staff could pick `toUnit` as this chemical's reporting-unit
 * override, given the same three preconditions the resolver enforces before it
 * will save one: a unit the facility's jurisdiction permits, a usable density
 * whenever the conversion crosses mass and volume, and an exact amount to
 * convert (not a storage-range code with none). This is the single check both
 * the FormSelect's per-option disabling and the blocking validation issue call,
 * so neither can disagree with the other, or with the resolver — which runs the
 * identical `canApplyReportingUnitOverride` predicate.
 */
export function canOverrideReportingUnit(
  rfc: Rfc | null | undefined,
  toUnit: UnitType
): CanApplyReportingUnitOverrideResult {
  const toPrismaUnit = toPrismaUnitType(toUnit);
  if (!toPrismaUnit) {
    // Every candidate this is called with comes from the UnitType enum
    // FormSelect iterates, so this narrows to null only on a drift between
    // that enum and encamp-prisma's — treat as unblocked rather than
    // silently disabling every option over a mismatch the type system should
    // have caught first.
    return { ok: true };
  }

  return canApplyReportingUnitOverride({
    // The pre-override unit/amounts, not the already-overridden ones: using
    // an already-converted figure as the source would check convertibility
    // from the wrong base. Same reasoning as `currentUnit` in
    // StaffReportingUnitOverride.
    fromUnit: toPrismaUnitType(rfc?.preOverrideUnit ?? rfc?.unit),
    toUnit: toPrismaUnit,
    density: rfc?.chemical?.density ?? null,
    densityUnits: toPrismaDensityUnits(rfc?.chemical?.densityUnits),
    // The facility's own state decides which units its Tier II jurisdiction
    // accepts; state of matter only matters for Oregon, whose list varies by it.
    state: rfc?.facility?.state ?? null,
    stateOfMatter: toPrismaStateOfMatter(rfc?.chemical?.stateOfMatter),
    manuallyEdited: rfc?.manuallyEdited,
    rfc: rfc
      ? {
          maxAmount: rfc.preOverrideMaxAmount ?? rfc.maxAmount,
          averageAmount: rfc.preOverrideAverageAmount ?? rfc.averageAmount,
          maxAmountLargestContainer: rfc.maxAmountLargestContainer,
          maxAmountCode: rfc.maxAmountCode,
          averageAmountCode: rfc.averageAmountCode,
          // An override is refused outright while any other aggregation error
          // stands, so the predicate needs to see it.
          aggregationErrorMessage: rfc.aggregationErrorMessage,
        }
      : null,
  });
}

/**
 * The permitted units, pretty-printed for a message. Empty is possible and
 * meaningful — Oregon permits nothing at all when the chemical has no state of
 * matter recorded, since its list is derived from it.
 */
// Typed as plain strings rather than either UnitType: the predicate lives in
// encamp-shared and hands back encamp-prisma's UnitType, which carries members
// (BARRELS) the GraphQL enum does not, so narrowing here would be a lie. Only
// display is needed.
function listAllowedUnits(allowedUnits: readonly string[]): string {
  if (allowedUnits.length === 0) {
    return "none, until the chemical's state of matter is recorded";
  }
  return allowedUnits.map((unit) => prettyPrintEnumValue(unit)).join(", ");
}

/** Full sentence for the blocking validation issue attached to the field. */
export function describeReportingUnitOverrideRefusal(
  toUnit: UnitType,
  result: OverrideRefusal
): string {
  const targetUnit = prettyPrintEnumValue(toUnit);
  if (result.reason === "jurisdictionDisallowsUnit") {
    return (
      `${result.state} does not accept ${targetUnit} as a Tier II reporting unit. ` +
      `Permitted units: ${listAllowedUnits(result.allowedUnits)}.`
    );
  }
  if (result.reason === "missingDensity") {
    return (
      `Cannot convert to ${targetUnit} — this chemical has no usable catalog density. ` +
      `Add a density in the catalog, or choose a different unit.`
    );
  }
  if (result.reason === "existingAggregationError") {
    return (
      `This chemical already has an aggregation error, and that error is reported instead of ` +
      `an override. Resolve it first, then set the override.`
    );
  }
  if (result.reason === "manuallyEdited") {
    return (
      `This chemical's reported amounts have been edited by hand, so aggregation will not ` +
      `rewrite them and the override could never take effect.`
    );
  }
  if (result.reason === "unknownSourceUnit") {
    return (
      `This chemical has reported amounts but no unit to convert them from, so there is ` +
      `nothing to convert into ${targetUnit}.`
    );
  }
  if (result.reason === "unknownJurisdiction") {
    return (
      `This facility has no state, so the storage range codes cannot be recalculated for ` +
      `${targetUnit}.`
    );
  }
  if (result.reason === "notConvertible") {
    return `Cannot convert this chemical's amounts to ${targetUnit}.`;
  }
  return (
    `The ${result.field} amount is reported as a storage range code rather than an exact ` +
    `amount, and a range cannot be converted. Enter an exact amount, or choose a different unit.`
  );
}

/** Short suffix appended to a disabled option's label in the unit FormSelect. */
export function describeReportingUnitOverrideOptionDisabledReason(result: OverrideRefusal): string {
  if (result.reason === "jurisdictionDisallowsUnit") {
    return `not accepted in ${result.state}`;
  }
  if (result.reason === "missingDensity") {
    return "needs a catalog density";
  }
  if (result.reason === "existingAggregationError") {
    return "chemical has an aggregation error";
  }
  if (result.reason === "manuallyEdited") {
    return "amounts were edited by hand";
  }
  if (result.reason === "unknownSourceUnit") {
    return "no unit to convert from";
  }
  if (result.reason === "unknownJurisdiction") {
    return "facility has no state";
  }
  if (result.reason === "notConvertible") {
    return "cannot be converted";
  }
  return `${result.field} amount is a range code, not exact`;
}
